import React from "react";

const Pagination = ({
  page,
  totalPages,
  totalRecords,
  onPageChange,
}) => {
  const safeTotalPages = Math.max(totalPages || 1, 1);
  // Callers that do not track a total left this undefined, which rendered as a
  // bare " Records" with no number.
  const safeTotalRecords = Number.isFinite(totalRecords) ? totalRecords : null;

  // Nothing to page through and nothing to count — the bar was pure noise
  // under every empty table.
  if (safeTotalPages <= 1 && !safeTotalRecords) return null;

  const getPages = () => {
    const pages = [];

    if (safeTotalPages <= 7) {
      for (let i = 1; i <= safeTotalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (page > 3) {
      pages.push("...");
    }

    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(safeTotalPages - 1, page + 1);
      i++
    ) {
      pages.push(i);
    }

    if (page < safeTotalPages - 2) {
      pages.push("...");
    }

    pages.push(safeTotalPages);

    return pages;
  };

  const isPaginationDisabled =
    totalRecords === 0 || safeTotalPages <= 1;

  return (
    <div
      className="flex items-center justify-between px-4 py-3 mt-3 rounded-lg border"
      style={{
        background: "var(--card-bg)",
        borderColor: "var(--card-border)",
      }}
    >
      {/* Left Side */}
      <div>
        <p
          className="text-sm font-medium"
          style={{ color: "var(--main-text)" }}
        >
          Page {page} of {safeTotalPages}
        </p>

        {safeTotalRecords !== null && (
          <p
            className="text-xs"
            style={{ color: "var(--main-text-muted)" }}
          >
            {safeTotalRecords} {safeTotalRecords === 1 ? "record" : "records"}
          </p>
        )}
      </div>

      {/* Page controls are pointless with a single page, but the record count
          above is still worth showing. */}
      {safeTotalPages > 1 && (
      <div className="flex items-center gap-0.5 justify-end">
        {/* First */}
        <button
          onClick={() => onPageChange(1)}
          disabled={isPaginationDisabled || page <= 1}
          className={`px-1.5 py-1 rounded border text-[11px] transition-all ${isPaginationDisabled || page <= 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:shadow-sm"
            }`}
          style={{
            background: "var(--card-bg)",
            borderColor: "var(--card-border)",
            color: "var(--main-text)",
          }}
        >
          {"<<"}
        </button>

        {/* Previous */}
        <button
          onClick={() => page > 1 && onPageChange(page - 1)}
          disabled={isPaginationDisabled || page <= 1}
          className={`px-1.5 py-1 rounded border text-[11px] transition-all ${isPaginationDisabled || page <= 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:shadow-sm"
            }`}
          style={{
            background: "var(--card-bg)",
            borderColor: "var(--card-border)",
            color: "var(--main-text)",
          }}
        >
          Prev
        </button>

        {/* Page Numbers */}
        {
          getPages().map((pageNumber, index) => {
            if (pageNumber === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="px-0.5 text-[11px]"
                  style={{
                    color: "var(--main-text-muted)",
                  }}
                >
                  ...
                </span>
              );
            }

            return (
              <button
                key={pageNumber}
                onClick={() => onPageChange(pageNumber)}
                className={`w-7 h-7 rounded text-[11px] font-medium transition-all ${page === pageNumber
                    ? "bg-brand text-white"
                    : "hover:shadow-sm"
                  }`}
                style={
                  page !== pageNumber
                    ? {
                      background: "var(--card-bg)",
                      border:
                        "1px solid var(--card-border)",
                      color: "var(--main-text)",
                    }
                    : {}
                }
              >
                {pageNumber}
              </button>
            );
          })}

        {/* Next */}
        <button
          onClick={() =>
            page < safeTotalPages &&
            onPageChange(page + 1)
          }
          disabled={
            isPaginationDisabled ||
            page >= safeTotalPages
          }
          className={`px-1.5 py-1 rounded border text-[11px] transition-all ${isPaginationDisabled ||
              page >= safeTotalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:shadow-sm"
            }`}
          style={{
            background: "var(--card-bg)",
            borderColor: "var(--card-border)",
            color: "var(--main-text)",
          }}
        >
          Next
        </button>

        {/* Last */}
        <button
          onClick={() =>
            onPageChange(safeTotalPages)
          }
          disabled={
            isPaginationDisabled ||
            page >= safeTotalPages
          }
          className={`px-1.5 py-1 rounded border text-[11px] transition-all ${isPaginationDisabled ||
              page >= safeTotalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:shadow-sm"
            }`}
          style={{
            background: "var(--card-bg)",
            borderColor: "var(--card-border)",
            color: "var(--main-text)",
          }}
        >
          {">>"}
        </button>
      </div>
      )}
    </div>
  );
};

export default Pagination;